"use client";

import SiteAnchor from "@/components/ui/SiteAnchor";

import { motion } from "motion/react";
import { Users, Radio } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { reactWebAuthHref } from "@/lib/reactWebAuthUrl";

const CLIENT_ICONS = ["🔍", "💰", "📋", "✅"] as const;
const PILOT_ICONS = ["📝", "🎯", "📈", "💳"] as const;

const CLIENT_KEYS = ["F1", "F2", "F3", "F4"] as const;
const PILOT_KEYS = ["F1", "F2", "F3", "F4"] as const;

export default function FeaturesGridSection() {
  const t = useTranslations("Features");
  const locale = useLocale();
  const signupHref = reactWebAuthHref(locale, "signup");

  return (
    <div className="bg-[#1e3a5f]/10 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-bold text-gray-900">{t("title")}</h2>
          <p className="mx-auto max-w-2xl text-xl text-gray-600">{t("subtitle")}</p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card className="h-full p-8">
              <div className="mb-6 flex items-center">
                <Users className="mr-3 h-8 w-8 text-primary" />
                <h3 className="text-2xl font-bold text-gray-900">{t("forBusinessTitle")}</h3>
              </div>
              <p className="mb-6 text-gray-600">{t("forBusinessIntro")}</p>
              <div className="mb-8 space-y-4">
                {CLIENT_KEYS.map((k, i) => (
                  <div key={k} className="flex items-start">
                    <span className="mr-3 text-2xl">{CLIENT_ICONS[i]}</span>
                    <div>
                      <h4 className="mb-1 font-semibold text-gray-900">{t(`client${k}Title`)}</h4>
                      <p className="text-sm text-gray-600">{t(`client${k}Desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                <SiteAnchor href={signupHref}>{t("ctaBusiness")}</SiteAnchor>
              </Button>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card className="h-full p-8">
              <div className="mb-6 flex items-center">
                <Radio className="mr-3 h-8 w-8 text-accent" />
                <h3 className="text-2xl font-bold text-gray-900">{t("forPilotsTitle")}</h3>
              </div>
              <p className="mb-6 text-gray-600">{t("forPilotsIntro")}</p>
              <div className="mb-8 space-y-4">
                {PILOT_KEYS.map((k, i) => (
                  <div key={k} className="flex items-start">
                    <span className="mr-3 text-2xl">{PILOT_ICONS[i]}</span>
                    <div>
                      <h4 className="mb-1 font-semibold text-gray-900">{t(`pilot${k}Title`)}</h4>
                      <p className="text-sm text-gray-600">{t(`pilot${k}Desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button className="w-full bg-accent text-white hover:bg-accent/90" asChild>
                <SiteAnchor href={signupHref}>{t("ctaPilots")}</SiteAnchor>
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
